import { FeatureFlag, FeatureFlagSource } from './feature-flag-enums';

export const FEATURE_FLAGS_STORAGE_KEY = 'eraFeatureFlags';

export type FeatureFlagState = Partial<Record<FeatureFlag, boolean>>;

export type FeatureFlagStatus = {
  value: boolean;
  source: FeatureFlagSource;
};

/** Server values win over client values, so `null`/absent means "defer to the client". Keys are numeric `FeatureFlag` values (usually) */
export type FeatureFlagsDto = Record<string, Nullable<boolean>>;

const listeners = new Set<() => void>();

/** Accepts a numeric enum value or its numeric string form (JSON object keys are always strings). */
const toFeatureFlag = (key: FeatureFlag | number | string): Optional<FeatureFlag> => {
  const numeric = typeof key === 'number' ? key : Number(key);
  return Number.isInteger(numeric) && numeric in FeatureFlag ? numeric : undefined;
};

/**
 * Filters an incoming flags object down to boolean values keyed by numeric flag ids. When
 * `keepUnknown` is false (the default), keys with no matching `FeatureFlag` enum member are dropped.
 * Generally this is the preferred option as we only ever care about unknown flags when we're trying
 * to pin down why something may or may not be working as expected.
 */
const normaliseFlags = (
  source: Record<string, unknown>,
  keepUnknown = false
): Record<number, boolean> =>
  Object.entries(source).reduce<Record<number, boolean>>((flags, [key, value]) => {
    const flag = keepUnknown ? Number(key) : toFeatureFlag(key);
    if (flag !== undefined && Number.isInteger(flag) && typeof value === 'boolean') {
      flags[flag] = value;
    }
    return flags;
  }, {});

const readClientFlags = (): FeatureFlagState => {
  try {
    const stored = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!stored) {
      return {};
    }

    const parsed = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    return normaliseFlags(parsed as Record<string, unknown>) as FeatureFlagState;
  } catch {
    return {};
  }
};

let serverFlags: Record<number, boolean> = {};
let clientFlags: FeatureFlagState = readClientFlags();

const notify = () => listeners.forEach((listener) => listener());

export const getFeatureFlag = (flag: FeatureFlag | number): boolean => {
  const resolved = toFeatureFlag(flag);
  if (resolved === undefined) {
    return false;
  }

  const serverValue = serverFlags[resolved];
  if (typeof serverValue === 'boolean') {
    return serverValue;
  }

  return clientFlags[resolved] ?? false;
};

export const getAllFeatureFlags = (): Record<FeatureFlag, FeatureFlagStatus> =>
  Object.values(FeatureFlag)
    .filter((value): value is FeatureFlag => typeof value === 'number')
    .reduce((statuses, flag) => {
      const serverValue = serverFlags[flag];
      const clientValue = clientFlags[flag];

      if (typeof serverValue === 'boolean') {
        statuses[flag] = { value: serverValue, source: FeatureFlagSource.Server };
      } else if (typeof clientValue === 'boolean') {
        statuses[flag] = { value: clientValue, source: FeatureFlagSource.Client };
      } else {
        statuses[flag] = { value: false, source: FeatureFlagSource.Unset };
      }

      return statuses;
    }, {} as Record<FeatureFlag, FeatureFlagStatus>);

export const setFeatureFlag = (flag: FeatureFlag | number, isEnabled: boolean): void => {
  const resolved = toFeatureFlag(flag);
  if (resolved === undefined) {
    return;
  }

  clientFlags = { ...clientFlags, [resolved]: isEnabled };

  try {
    window.localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(clientFlags));
  } catch {
    // Storage being unavailable shouldn't stop the flag applying for this session.
  }

  notify();
};

export const setServerFeatureFlags = (flags: Optional<Nullable<FeatureFlagsDto>>): void => {
  serverFlags = flags ? normaliseFlags(flags, true) : {};
  notify();
};

/** Server-set flags with no matching `FeatureFlag` enum member, e.g. keys added server-side ahead of the frontend. */
export const getUnknownServerFeatureFlags = (): Record<number, boolean> =>
  Object.entries(serverFlags).reduce<Record<number, boolean>>((flags, [key, value]) => {
    const numeric = Number(key);
    if (!(numeric in FeatureFlag)) {
      flags[numeric] = value;
    }
    return flags;
  }, {});

export const subscribeToFeatureFlags = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setupFeatureFlagBindings = () => {
  window.getFeatureFlag = getFeatureFlag;
  window.setFeatureFlag = setFeatureFlag;
  window.getAllFeatureFlags = getAllFeatureFlags;
};

// Just an alias to tidy up imports for consumers.
export const FeatureFlags = FeatureFlag;
